import type { Object as FabricObject } from 'fabric/fabric-impl';
import type Interactive from 'modules/overlay-canvas/canvases/interactive';
import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * Живые объекты канвы редактора табло: менеджер Fabric и текущее выделение.
 *
 * Раньше они лежали в Redux (`templateEditor.editor`), но это экземпляры классов — Redux
 * их не сериализует, DevTools ломаются, а проверка сериализуемости ругалась на каждом
 * экшене (IMGM-1550). Читает их только дерево `board-editor.tsx`, поэтому хватает контекста.
 *
 * Три контекста, а не один объект: смена выделения не должна перерисовывать ~30 читателей
 * менеджера, а компоненты, которые только пишут, не должны перерисовываться вовсе
 * (https://react.dev/reference/react/useContext#optimizing-re-renders-when-passing-objects-and-functions).
 */

const EMPTY_SELECTION: FabricObject[] = [];

interface EditorCanvasActions {
  setManager: (manager: Interactive | undefined) => void;
  setActiveObjects: (objects: FabricObject[]) => void;
}

const ManagerContext = createContext<Interactive | undefined>(undefined);
const ActiveObjectsContext = createContext<FabricObject[]>(EMPTY_SELECTION);
const ActionsContext = createContext<EditorCanvasActions | undefined>(undefined);

export interface EditorCanvasProviderProps {
  children: React.ReactNode;
}

export function EditorCanvasProvider({ children }: EditorCanvasProviderProps) {
  const [manager, setManager] = useState<Interactive | undefined>(undefined);
  const [activeObjects, setActiveObjects] = useState<FabricObject[]>(EMPTY_SELECTION);
  // Сеттеры useState стабильны, значит и объект действий не меняется между рендерами.
  const actions = useMemo<EditorCanvasActions>(() => ({ setManager, setActiveObjects }), []);

  return (
    <ActionsContext.Provider value={actions}>
      <ManagerContext.Provider value={manager}>
        <ActiveObjectsContext.Provider value={activeObjects}>{children}</ActiveObjectsContext.Provider>
      </ManagerContext.Provider>
    </ActionsContext.Provider>
  );
}

/** Менеджер канвы; `undefined`, пока канва не создана или вне `EditorCanvasProvider`. */
export function useEditorManager(): Interactive | undefined {
  return useContext(ManagerContext);
}

/** Выделенные объекты канвы; вне провайдера — пустой список. */
export function useEditorActiveObjects(): FabricObject[] {
  return useContext(ActiveObjectsContext);
}

const NOOP_ACTIONS: EditorCanvasActions = { setManager: () => undefined, setActiveObjects: () => undefined };

/**
 * Запись менеджера и выделения. Вне провайдера — пустые функции: панели редактора рендерятся
 * в тестах и без канвы, как раньше без менеджера в Redux.
 */
export function useEditorCanvasActions(): EditorCanvasActions {
  return useContext(ActionsContext) ?? NOOP_ACTIONS;
}
